import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import {
  getObjectiveTypeTitles,
  deleteObjectiveTypeTitle,
} from "services/objectiveTypeTitleService";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
}));

const columns = [
  {
    id: "objectiveType",
    label: "Objective Type",
    type: "string",
    isSortable: true,
    alignHead: "center",
    alignBody: "center",
  },
  {
    id: "titleTemplate",
    label: "Title Template",
    type: "string",
    isSortable: true,
    alignHead: "center",
    alignBody: "center",
  },
  {
    id: "translationCount",
    label: "Translations",
    type: "string",
    isSortable: true,
    alignHead: "center",
    alignBody: "center",
  },
  { id: "actions", label: "Actions" },
];

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDelete?: (data: any) => void;
  };
}

const RowActions = ({
  data,
  actionHandlers: { handleDelete = () => {} },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.OBJECTIVE_TYPE_TITLES.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={(e: any) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate(
              configData.routes.challenges.editObjectiveTypeTitles?.replace(
                ":id",
                data?._id,
              ),
            );
          }}
        >
          Edit
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              handleDelete(data);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const ObjectiveTypeTitles = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<any[]>([]);
  const [orderDirection, setOrderDirection] = useState("asc");

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.OBJECTIVE_TYPE_TITLES.UPDATE_ROLES,
  );

  const navigateToCreate = () =>
    navigate(configData.routes.challenges.createObjectiveTypeTitles);

  const onColumnClick = (column: any) => {
    const _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onDelete = async () => {
    try {
      const resp = await deleteObjectiveTypeTitle(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error: any) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({
      itemName: data?.objectiveType ?? data?._id,
      id: data?._id,
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getObjectiveTypeTitles();
      const items = resp?.data || [];
      setListData(
        items.map((item: any) => ({
          ...item,
          translationCount: item.titleTemplateTrans
            ? Object.keys(item.titleTemplateTrans).length
            : 0,
        })),
      );
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredColumns = columns.filter(
    (col) => col.id !== "actions" || hasEditPermission,
  );

  return (
    <ContentShell
      title="Challenge Titles"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          {sessionHelper.hasRole(
            PermittedRoles.OBJECTIVE_TYPE_TITLES.CREATE_ROLES,
          ) && <CustomButton onClick={navigateToCreate}>Create</CustomButton>}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Table
          columns={filteredColumns}
          data={listData}
          hidePagination={true}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          actionHandlers={{
            handleDelete: _openConsentDialog,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default ObjectiveTypeTitles;
